import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import {
  getNotifications,
  getUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
} from "../helpers/notification_helper";
import { registerDesktopPushSubscription } from "../helpers/desktop_notification_helper";
import { getAuthenticatedUser } from "../helpers/auth-session";
import { BASE_URL } from "../helpers/url_helper";
import { parseUtcDateTime } from "../helpers/utc-date";

const NotificationContext = createContext(null);

const hubUrl = () =>
  `${BASE_URL.replace(/\/api\/v1\/?$/, "")}/hubs/notifications`;

export const relativeTime = (value) => {
  if (!value) return "";
  const seconds = Math.max(
    0,
    Math.floor((Date.now() - parseUtcDateTime(value).getTime()) / 1000)
  );
  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400)
    return `${Math.floor(seconds / 3600)} hour${
      Math.floor(seconds / 3600) === 1 ? "" : "s"
    } ago`;
  if (seconds < 172800) return "Yesterday";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(parseUtcDateTime(value));
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const registeredPushToken = useRef("");
  const isAuthenticationRoute = [
    "/",
    "/signin",
    "/login",
    "/twostepverification",
    "/forgot-password",
    "/logout",
    "/switch-firm",
  ].includes(location.pathname);

  const refresh = useCallback(async () => {
    if (isAuthenticationRoute || !getAuthenticatedUser() || getAuthenticatedUser()?.requiresFirmSelection) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    setLoading(true);
    try {
      const [list, count] = await Promise.all([
        getNotifications({ page: 1, pageSize: 10 }),
        getUnreadNotificationCount(),
      ]);
      if (list?.status) setNotifications(list.data?.items || []);
      if (count?.status) setUnreadCount(count.data?.count || 0);
    } catch {
      // Background refresh must not prevent selecting a firm or using the page.
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticationRoute]);

  const receive = useCallback((notification) => {
    setNotifications((current) =>
      [
        notification,
        ...current.filter((item) => item.id !== notification.id),
      ].slice(0, 10)
    );
    if (!notification.isRead) setUnreadCount((count) => count + 1);
    if (document.visibilityState === "visible") {
      toast.info(
        <div>
          <strong>{notification.title}</strong>
          <div>{notification.message}</div>
        </div>,
        { toastId: `notification-${notification.id}` }
      );
    }
  }, []);

  const markRead = useCallback(
    async (id) => {
      const existing = notifications.find((item) => item.id === id);
      if (!existing || existing.isRead) return;
      setNotifications((current) =>
        current.map((item) =>
          item.id === id
            ? { ...item, isRead: true, readAt: new Date().toISOString() }
            : item
        )
      );
      setUnreadCount((count) => Math.max(0, count - 1));
      try {
        await markNotificationRead(id);
      } catch {
        setNotifications((current) =>
          current.map((item) => (item.id === id ? existing : item))
        );
        setUnreadCount((count) => count + 1);
      }
    },
    [notifications]
  );

  const markAllRead = useCallback(async () => {
    const unread = notifications.filter((item) => !item.isRead).length;
    setNotifications((current) =>
      current.map((item) => ({
        ...item,
        isRead: true,
        readAt: item.readAt || new Date().toISOString(),
      }))
    );
    setUnreadCount(0);
    try {
      await markAllNotificationsRead();
    } catch {
      if (unread) await refresh();
    }
  }, [notifications, refresh]);

  useEffect(() => {
    refresh();
  }, [refresh, location.pathname]);
  useEffect(() => {
    const userId = getAuthenticatedUser()?.userId;
    if (
      isAuthenticationRoute ||
      getAuthenticatedUser()?.requiresFirmSelection ||
      !userId ||
      registeredPushToken.current === String(userId)
    )
      return;
    registerDesktopPushSubscription()
      .then((result) => {
        if (result.registered) registeredPushToken.current = String(userId);
      })
      .catch(() => undefined);
  }, [isAuthenticationRoute, location.pathname]);
  useEffect(() => {
    if (isAuthenticationRoute || !getAuthenticatedUser() || getAuthenticatedUser()?.requiresFirmSelection) return undefined;
    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl(), {
        withCredentials: true,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();
    connection.on("notificationCreated", receive);
    connection.start().catch(() => undefined);
    return () => {
      connection.off("notificationCreated", receive);
      connection.stop().catch(() => undefined);
    };
  }, [isAuthenticationRoute, receive, location.pathname]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      loading,
      refresh,
      markRead,
      markAllRead,
    }),
    [notifications, unreadCount, loading, refresh, markRead, markAllRead]
  );
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context)
    throw new Error(
      "useNotifications must be used inside NotificationProvider."
    );
  return context;
};
